import { BadgeCheck, Palette, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { appConfig } from "@/config/app";
import { BRAND } from "../brand";
import { CtaLink } from "../CtaLink";
import { SectionHeading } from "../SectionHeading";
import { ParallaxLayer } from "../motion/ParallaxLayer";
import { useMediaQuery } from "../motion/useMediaQuery";
import { Reveal } from "../motion/Reveal";
import { WhiteLabelDemo } from "../demo/WhiteLabelDemo";

const POINTS: Array<{ Icon: LucideIcon; title: string; text: string }> = [
  {
    Icon: Palette,
    title: "Votre nom, votre logo, vos couleurs",
    text: "L’application, l’écran de connexion et l’onglet du navigateur portent votre marque. Deux couleurs suffisent\u00a0: la marque, les boutons et les surlignages suivent.",
  },
  {
    Icon: BadgeCheck,
    title: "Des repères qui ne bougent pas",
    text: "Retards, promesses et alertes gardent leurs couleurs sous toutes les marques.",
  },
  {
    Icon: Send,
    title: "L’expéditeur reste l’entreprise",
    text: "Chaque relance part de la boîte Gmail de l’entreprise qui relance.",
  },
];

/** Cabinets et partenaires : la marque blanche, démontrée. */
export function ForFirms() {
  // Sous 1024 px, les feuilles ne se voient pas : ni montées, ni suivies au défilement.
  const wide = useMediaQuery("(min-width: 1024px)");
  return (
    <section
      id="cabinets"
      className="relative overflow-hidden bg-primary py-24 text-primary-foreground lg:py-32"
    >
      {/* Les deux feuilles du logo, en filigrane : celle du fond glisse
          moins vite que l'autre, le dossier prend du relief au défilement. */}
      {wide && (
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-28">
          <ParallaxLayer distance={110} className="h-[380px] w-[300px]">
            <div className="h-full w-full rotate-[14deg] rounded-sheet bg-logo-sheet-back opacity-[0.07]" />
          </ParallaxLayer>
          <ParallaxLayer distance={50} className="absolute top-10 -left-16 h-[380px] w-[300px]">
            <div className="h-full w-full -rotate-[6deg] rounded-sheet bg-logo-sheet-front opacity-[0.06]" />
          </ParallaxLayer>
        </div>
      )}

      <div className="relative mx-auto grid max-w-[1200px] gap-14 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:px-8">
        <div>
          <SectionHeading
            tone="dark"
            eyebrow="Cabinets et partenaires"
            title="À votre nom, pour vos clients."
            lead={`Proposez ${BRAND} sous votre nom, avec votre logo et vos couleurs. Les relances, elles, partent toujours de la boîte de l’entreprise qui relance.`}
          />
          <ul className="mt-10 space-y-6">
            {POINTS.map(({ Icon, title, text }, i) => (
              <Reveal as="li" key={title} delay={120 + i * 110} className="flex gap-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-full border border-primary-foreground/20 text-action-on-primary">
                  <Icon aria-hidden className="size-[18px]" strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-primary-foreground">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-primary-foreground-muted">{text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={480} className="mt-10">
            <CtaLink to={`mailto:${appConfig.partnersEmail}`} variant="onDark" arrow>
              Parler du programme cabinets
            </CtaLink>
          </Reveal>
        </div>
        <Reveal delay={160}>
          <WhiteLabelDemo />
        </Reveal>
      </div>
    </section>
  );
}
